import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { ArrowBack, Cancel, CheckCircle, Close } from '@mui/icons-material';
import { getRfidScanResultById } from '../../services/api';
import CopyableTag from '../Shared/CopyableTag';

const defaultSummary = {
  totalTagsScanned: 0,
  assignedTagsCount: 0,
  unassignedTagsCount: 0,
  uniqueSKUsFound: 0
};

const SKU_DETAILS_BATCH_SIZE = 25;
const SNAPSHOT_BATCH_SIZE = 100;
const createSnapshotFilter = () => ({
  id: `${Date.now()}-${Math.random()}`,
  locationId: '',
  operator: '>',
  qty: ''
});

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }, []);
};

const formatDuration = (seconds = 0) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
};

const normalizeRecord = (item) => {
  if (!item) return null;
  const clientId = item.scanInfo?.clientId || item.metadata?.clientId || item.client?.id || 'N/A';
  const clientName =
    item.scanInfo?.clientName ||
    item.metadata?.clientName ||
    item.client?.name ||
    item.clientInfo?.name ||
    (clientId !== 'N/A' ? `Client ${clientId}` : 'Unknown client');

  return {
    id: item.id,
    clientId,
    clientName,
    performedBy: item.scannedBy || item.scanInfo?.performedBy || item.metadata?.performedBy || 'Unknown',
    scanTimestamp: item.dateTime || item.createdAt,
    totalSKU: item.metadata?.totalSKU || 0,
    scannedSKU: item.summary?.uniqueSKUsFound || 0,
    totalRfidTags: item.metadata?.totalUnit || 0,
    scannedRfidTags: item.summary?.totalTagsScanned || 0,
    unassignedTags: item.summary?.unassignedTagsCount || 0,
    matchesWarehouse: item.status === 'Match',
    deviceId: item.scanInfo?.deviceId || 'N/A',
    scanDuration: item.scanInfo?.scanDuration || 0,
    summary: { ...defaultSummary, ...(item.summary || {}) },
    skuResults: item.skuResults || [],
    missingSKUs: item.missingSKUs || [],
    unassignedTagsList: item.unassignedTags || [],
    // `tagsMismatchLocation` is the field name actually returned by the API;
    // `tagWrongLocation` is kept only as a fallback for older records.
    tagWrongLocationList: item.tagsMismatchLocation || item.tagWrongLocation || [],
    allScannedTagsList: item.allScannedTags?.map(t => t.tagId || t) || [],
    inventory_snapshot: item.inventory_snapshot
  };
};

const buildFallbackSnapshot = (record) => {
  const skuMap = new Map();
  const scanLocationId = record.clientId === 'N/A' ? 'scanned' : String(record.clientId);
  const scanLocationName = record.clientName || 'Scanned Location';

  const addSku = (skuResult, serialSource = 'scannedTags') => {
    const sku = skuResult.skuCode || skuResult.skuData?.sku;
    if (!sku) return;
    const serials = (skuResult[serialSource] || [])
      .map(tag => (typeof tag === 'string' ? tag : tag?.tagId))
      .filter(Boolean);
    if (!skuMap.has(sku)) {
      skuMap.set(sku, {
        sku,
        name: skuResult.skuData?.name || sku,
        totalQty: 0,
        locations: {}
      });
    }
    const item = skuMap.get(sku);
    if (!item.locations[scanLocationId]) {
      item.locations[scanLocationId] = {
        locationId: scanLocationId,
        locationName: scanLocationName,
        qty: 0,
        serials: []
      };
    }
    item.locations[scanLocationId].qty += serials.length;
    item.locations[scanLocationId].serials.push(...serials);
    item.totalQty += serials.length;
  };

  (record.skuResults || []).forEach(sku => addSku(sku, 'scannedTags'));
  (record.missingSKUs || []).forEach(sku => addSku(sku, 'expectedTags'));

  return {
    capturedAt: record.scanTimestamp,
    locations: [{ id: scanLocationId, name: scanLocationName }],
    items: Array.from(skuMap.values())
  };
};

const getTagId = (tag) => {
  if (typeof tag === 'string') return tag;
  return tag?.tagId || tag?.serial || tag?.serialNumber || '';
};

const InventoryCountDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [unassignedTab, setUnassignedTab] = useState(0);
  const [serialDialog, setSerialDialog] = useState(null);
  const [visibleSkuCount, setVisibleSkuCount] = useState(SKU_DETAILS_BATCH_SIZE);
  const [visibleSnapshotCount, setVisibleSnapshotCount] = useState(SNAPSHOT_BATCH_SIZE);
  const skuLoadMoreRef = useRef(null);
  const snapshotLoadMoreRef = useRef(null);
  const snapshotTableContainerRef = useRef(null);
  const [clientNameMap, setClientNameMap] = useState({});
  const [snapshotFilters, setSnapshotFilters] = useState([createSnapshotFilter()]);
  const [onlyAllLocationsGreaterThanOne, setOnlyAllLocationsGreaterThanOne] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const rawMeta = localStorage.getItem('appMetaData');
      if (!rawMeta) return;
      const stores = JSON.parse(rawMeta)?.storeList || [];
      const map = stores.reduce((acc, store) => {
        if (store?.id !== undefined && store?.id !== -1) {
          acc[String(store.id)] = store.name || `Client ${store.id}`;
        }
        return acc;
      }, {});
      setClientNameMap(prev => (
        JSON.stringify(prev) === JSON.stringify(map) ? prev : map
      ));
    } catch (err) {
      console.error('Failed to parse client metadata from localStorage:', err);
    }
  }, []);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await getRfidScanResultById(id);
        setRecord(normalizeRecord(response));
      } catch (err) {
        console.error('Failed to load inventory count detail:', err);
        setError('Failed to load inventory count detail.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDetail();
  }, [id]);

  const scannedSkuResults = useMemo(() => {
    if (!record) return [];
    return record.skuResults.filter((sku) => {
      const scannedCount = sku.scannedCount ?? sku.scannedTags?.length ?? 0;
      return scannedCount > 0;
    });
  }, [record]);

  const visibleScannedSkuResults = useMemo(
    () => scannedSkuResults.slice(0, visibleSkuCount),
    [scannedSkuResults, visibleSkuCount]
  );

  const hasMoreScannedSkuResults = visibleSkuCount < scannedSkuResults.length;

  useEffect(() => {
    setVisibleSkuCount(SKU_DETAILS_BATCH_SIZE);
  }, [id]);

  useEffect(() => {
    if (tab !== 1 || !hasMoreScannedSkuResults) return undefined;
    const target = skuLoadMoreRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleSkuCount((current) => Math.min(current + SKU_DETAILS_BATCH_SIZE, scannedSkuResults.length));
        }
      },
      { rootMargin: '360px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [tab, hasMoreScannedSkuResults, scannedSkuResults.length]);

  const missingSkuResults = useMemo(() => {
    if (!record) return [];
    const filtered = record.skuResults.filter((sku) => {
      const scannedCount = sku.scannedCount ?? sku.scannedTags?.length ?? 0;
      return scannedCount === 0;
    });
    return filtered.length > 0 ? filtered : record.missingSKUs || [];
  }, [record]);

  const snapshot = useMemo(() => {
    if (!record) return { locations: [], items: [] };
    const rawSnapshot = record.inventory_snapshot?.items ? record.inventory_snapshot : buildFallbackSnapshot(record);
    const locations = (rawSnapshot.locations || []).map((location) => {
      const key = String(location.id);
      return {
        ...location,
        name: clientNameMap[key] || location.name || `Client ${key}`
      };
    });
    const locationNameById = locations.reduce((acc, location) => {
      acc[String(location.id)] = location.name;
      return acc;
    }, {});
    const items = (rawSnapshot.items || []).map((item) => {
      const itemLocations = Object.entries(item.locations || {}).reduce((acc, [locationId, value]) => {
        acc[String(locationId)] = {
          ...value,
          locationName: locationNameById[String(locationId)] || value.locationName || `Client ${locationId}`
        };
        return acc;
      }, {});
      return {
        ...item,
        locations: itemLocations
      };
    });
    return {
      ...rawSnapshot,
      locations,
      items
    };
  }, [record, clientNameMap]);

  const snapshotSkuMap = useMemo(() => {
    const map = new Map();
    (snapshot.items || []).forEach(item => {
      if (item?.sku) {
        map.set(item.sku, item);
      }
    });
    return map;
  }, [snapshot.items]);

  const currentLocationId = useMemo(() => (
    record?.clientId && record.clientId !== 'N/A' ? String(record.clientId) : ''
  ), [record]);

  const serialSnapshotMap = useMemo(() => {
    const map = new Map();
    (snapshot.items || []).forEach((item) => {
      Object.entries(item.locations || {}).forEach(([locationId, location]) => {
        (location.serials || []).forEach((serial) => {
          if (!serial) return;
          map.set(String(serial), {
            sku: item.sku,
            name: item.name,
            locationId: String(locationId),
            locationName: location.locationName || `Client ${locationId}`
          });
        });
      });
    });
    return map;
  }, [snapshot.items]);

  const categorizedUnassignedTags = useMemo(() => {
    const withProductOtherLocation = [];
    const withoutProductInfo = [];

    (record?.unassignedTagsList || []).forEach((tag) => {
      const rawTag = tag && typeof tag === 'object' ? tag : {};
      const tagId = getTagId(tag);
      const snapshotInfo = serialSnapshotMap.get(String(tagId));
      const normalizedTag = {
        ...rawTag,
        tagId,
        timestamp: rawTag.timestamp,
        skuCode: rawTag.skuCode || rawTag.sku || snapshotInfo?.sku,
        productName: rawTag.productName || rawTag.name || snapshotInfo?.name,
        locationId: rawTag.locationId ? String(rawTag.locationId) : snapshotInfo?.locationId,
        locationName: rawTag.locationName || snapshotInfo?.locationName
      };

      if (snapshotInfo || normalizedTag.skuCode || normalizedTag.productName) {
        withProductOtherLocation.push(normalizedTag);
      } else {
        withoutProductInfo.push(normalizedTag);
      }
    });

    return {
      withProductOtherLocation,
      withoutProductInfo
    };
  }, [record?.unassignedTagsList, serialSnapshotMap]);

  const displayClientName = useMemo(() => (
    record ? clientNameMap[String(record.clientId)] || record.clientName : ''
  ), [record, clientNameMap]);

  const skuHasOtherLocationStock = (skuCode) => {
    const snapshotItem = snapshotSkuMap.get(skuCode);
    if (!snapshotItem || !currentLocationId) return false;
    return Object.entries(snapshotItem.locations || {}).some(([locationId, value]) => (
      String(locationId) !== currentLocationId && Number(value?.qty || 0) !== 0
    ));
  };

  const snapshotRows = useMemo(() => {
    const activeFilters = snapshotFilters
      .map(filter => ({ ...filter, qtyValue: Number(filter.qty) }))
      .filter(filter => filter.locationId && filter.qty !== '' && !Number.isNaN(filter.qtyValue));

    return (snapshot.items || []).filter((item) => {
      if (onlyAllLocationsGreaterThanOne) {
        const hasLocations = (snapshot.locations || []).length > 0;
        const everyLocationGreaterThanOne = hasLocations && snapshot.locations.every((location) => {
          const qty = Number(item.locations?.[String(location.id)]?.qty || 0);
          return qty > 1;
        });
        if (!everyLocationGreaterThanOne) return false;
      }

      return activeFilters.every((filter) => {
        const qty = Number(item.locations?.[String(filter.locationId)]?.qty || 0);
        return filter.operator === '>' ? qty > filter.qtyValue : qty < filter.qtyValue;
      });
    });
  }, [snapshot.items, snapshot.locations, snapshotFilters, onlyAllLocationsGreaterThanOne]);

  const visibleSnapshotRows = useMemo(
    () => snapshotRows.slice(0, visibleSnapshotCount),
    [snapshotRows, visibleSnapshotCount]
  );

  const hasMoreSnapshotRows = visibleSnapshotCount < snapshotRows.length;

  useEffect(() => {
    setVisibleSnapshotCount(SNAPSHOT_BATCH_SIZE);
  }, [id, snapshotFilters, onlyAllLocationsGreaterThanOne]);

  useEffect(() => {
    if (tab !== 5 || !hasMoreSnapshotRows) return undefined;
    const target = snapshotLoadMoreRef.current;
    const root = snapshotTableContainerRef.current;
    if (!target || !root) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleSnapshotCount((current) => Math.min(current + SNAPSHOT_BATCH_SIZE, snapshotRows.length));
        }
      },
      { root, rootMargin: '240px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [tab, hasMoreSnapshotRows, snapshotRows.length]);

  const updateSnapshotFilter = (filterId, field, value) => {
    setSnapshotFilters(prev => prev.map(filter => (
      filter.id === filterId ? { ...filter, [field]: value } : filter
    )));
  };

  const removeSnapshotFilter = (filterId) => {
    setSnapshotFilters(prev => (
      prev.length > 1 ? prev.filter(filter => filter.id !== filterId) : [createSnapshotFilter()]
    ));
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !record) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/inventory-count-history')}>
          Back
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>{error || 'Record not found.'}</Typography>
      </Box>
    );
  }

  const renderSkuCards = (items, emptyText, danger = false) => (
    items.length > 0 ? items.map((skuResult) => {
      const hasOtherLocationStock = !danger && skuHasOtherLocationStock(skuResult.skuCode);
      return (
      <Paper
        key={skuResult.skuCode}
        sx={{
          mb: 2,
          p: 2,
          border: hasOtherLocationStock ? '2px solid #2563eb' : danger ? '1px solid #fca5a5' : '1px solid #e5e7eb',
          borderRadius: 2,
          backgroundColor: hasOtherLocationStock ? '#eff6ff' : danger ? '#fef2f2' : '#fff'
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {skuResult.skuData?.image && (
            <Box sx={{ width: 80, height: 80, flexShrink: 0, border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
              <img src={skuResult.skuData.image} alt={skuResult.skuData.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {skuResult.skuData?.name || skuResult.skuCode}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
              SKU: {skuResult.skuCode}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {skuResult.scannedCount !== undefined && <Chip label={`${skuResult.scannedCount} Scanned`} color="success" size="small" />}
              {skuResult.missingCount > 0 && <Chip label={`${skuResult.missingCount} Missing`} color="warning" size="small" />}
              {hasOtherLocationStock && <Chip label="Stock in other location" color="primary" size="small" />}
            </Stack>
          </Box>
        </Box>
        {['scannedTags', 'expectedTags', 'missingTags'].map((field) => {
          const tags = skuResult[field] || [];
          if (!tags.length) return null;
          const label = field === 'scannedTags' ? 'Scanned Tags' : field === 'expectedTags' ? 'Expected Tags' : 'Missing Tags';
          if (field === 'scannedTags') {
            return (
              <Box key={field} sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {label}: {tags.length} tags
                </Typography>
              </Box>
            );
          }
          return (
            <Box key={field} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {label} ({tags.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {tags.map((tag, index) => (
                  <CopyableTag
                    key={`${field}-${index}`}
                    text={typeof tag === 'string' ? tag : tag?.tagId}
                    variant="chip"
                    color={field === 'missingTags' ? 'warning' : danger ? 'error' : 'success'}
                    chipVariant="outlined"
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Paper>
      );
    }) : (
      <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 2 }}>
        {emptyText}
      </Typography>
    )
  );

  const renderUnknownTagList = (items, emptyText) => (
    items.length ? (
      <List sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}>
        {items.map((tag, index) => (
          <ListItem key={`${tag.tagId}-${index}`} sx={{ borderBottom: '1px solid #f3f4f6' }}>
            <ListItemText
              primary={<CopyableTag text={tag.tagId} variant="text" sx={{ fontWeight: 600 }} />}
              secondary={tag.timestamp ? `Scanned at ${new Date(tag.timestamp).toLocaleTimeString()}` : ''}
            />
            <ListItemSecondaryAction>
              <Chip label="No product info" color="warning" size="small" />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    ) : (
      <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 3 }}>
        {emptyText}
      </Typography>
    )
  );

  const renderKnownOtherLocationTags = (items) => (
    items.length ? (
      <TableContainer component={Paper} sx={{ border: '1px solid #e5e7eb' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>RFID Tag</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>SKU</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Product Location</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Scanned Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((tag, index) => (
              <TableRow key={`${tag.tagId}-${index}`} hover>
                <TableCell>
                  <CopyableTag text={tag.tagId} variant="text" sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell>{tag.skuCode || 'N/A'}</TableCell>
                <TableCell>{tag.productName || 'N/A'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2">{tag.locationName || 'N/A'}</Typography>
                    {tag.locationId && <Chip label={`ID: ${tag.locationId}`} size="small" variant="outlined" />}
                  </Stack>
                </TableCell>
                <TableCell>{tag.timestamp ? new Date(tag.timestamp).toLocaleTimeString() : 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 3 }}>
        No scanned tags with product information were found outside this location
      </Typography>
    )
  );

  const renderTagWrongLocationTags = (items) => (
    items.length ? (
      <TableContainer component={Paper} sx={{ border: '1px solid #e5e7eb' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 60 }}>Image</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>RFID Tag</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>SKU</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Owner Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((tag, index) => (
              <TableRow key={`${tag.tagId}-${index}`} hover>
                <TableCell>
                  {tag.image ? (
                    <Box
                      component="img"
                      src={tag.image}
                      alt={tag.productName || tag.sku || 'product'}
                      sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, border: '1px solid #e5e7eb' }}
                    />
                  ) : (
                    <Box sx={{ width: 40, height: 40, borderRadius: 1, backgroundColor: '#f1f5f9' }} />
                  )}
                </TableCell>
                <TableCell>
                  <CopyableTag text={tag.tagId} variant="text" sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell>{tag.sku || 'N/A'}</TableCell>
                <TableCell>{tag.productName || 'N/A'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2">{tag.ownerLocationName || 'N/A'}</Typography>
                    {(tag.ownerClientId || tag.ownerClientId === 0) && (
                      <Chip label={`ID: ${tag.ownerClientId}`} size="small" variant="outlined" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 3 }}>
        No tags belonging to another location were found
      </Typography>
    )
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/inventory-count-history')}>
          Back
        </Button>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Inventory Count Details</Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {record.id} - {formatDate(record.scanTimestamp)}
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 2 }}>
            <Tab label="Overview" />
            <Tab label="SKU Details" />
            <Tab label="Missing SKUs" />
            <Tab label="Unassigned Tags" />
            <Tab label="All Tags" />
            <Tab label="Inventory Snapshot" />
            <Tab label={`Wrong Location (${record.tagWrongLocationList.length})`} />
          </Tabs>
        </Box>

        <CardContent>
          {tab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Inventory Count Summary</Typography>
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Client</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{displayClientName}</Typography><Typography variant="caption" sx={{ color: '#94a3b8' }}>ID: {record.clientId}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Performed By</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{record.performedBy}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Device ID</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{record.deviceId}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Scan Duration</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDuration(record.scanDuration)}</Typography></Grid>
                </Grid>
              </Paper>
              <Grid container spacing={2}>
                {[
                  ['Total Tags Scanned', record.summary.totalTagsScanned, '#1e40af'],
                  ['Assigned Tags', record.summary.assignedTagsCount, '#16a34a'],
                  ['Unassigned Tags', record.summary.unassignedTagsCount, record.summary.unassignedTagsCount > 0 ? '#dc2626' : '#64748b'],
                  ['Unique SKUs Found', record.summary.uniqueSKUsFound, '#16a34a'],
                  ['Wrong Location Tags', record.tagWrongLocationList.length, record.tagWrongLocationList.length > 0 ? '#dc2626' : '#64748b']
                ].map(([label, value, color]) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Paper sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color, textAlign: 'center' }}>{value}</Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', mt: 1 }}>{label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Paper sx={{ p: 2, mt: 2, backgroundColor: record.matchesWarehouse ? '#f0fdf4' : '#fefce8', border: record.matchesWarehouse ? '1px solid #bbf7d0' : '1px solid #fde047' }}>
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <Typography variant="body2">SKUs Found: {record.scannedSKU} / {record.totalSKU}</Typography>
                  <Typography variant="body2">Tags Found: {record.scannedRfidTags} / {record.totalRfidTags}</Typography>
                  <Chip icon={record.matchesWarehouse ? <CheckCircle /> : <Cancel />} label={record.matchesWarehouse ? 'Match' : 'Mismatch'} color={record.matchesWarehouse ? 'success' : 'error'} size="small" />
                </Stack>
              </Paper>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Showing {Math.min(visibleSkuCount, scannedSkuResults.length)} / {scannedSkuResults.length} scanned SKUs
                </Typography>
                {hasMoreScannedSkuResults && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setVisibleSkuCount((current) => Math.min(current + SKU_DETAILS_BATCH_SIZE, scannedSkuResults.length))}
                  >
                    Load more
                  </Button>
                )}
              </Stack>
              {renderSkuCards(visibleScannedSkuResults, 'No scanned SKUs available for this count')}
              {hasMoreScannedSkuResults && (
                <Box ref={skuLoadMoreRef} sx={{ height: 24 }} />
              )}
            </Box>
          )}
          {tab === 2 && renderSkuCards(missingSkuResults, 'All SKUs in warehouse have been scanned', true)}
          {tab === 3 && (
            <Box>
              <Tabs
                value={unassignedTab}
                onChange={(e, value) => setUnassignedTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label={`Product in other location (${categorizedUnassignedTags.withProductOtherLocation.length})`} />
                <Tab label={`No product info (${categorizedUnassignedTags.withoutProductInfo.length})`} />
              </Tabs>

              {unassignedTab === 0 && renderKnownOtherLocationTags(categorizedUnassignedTags.withProductOtherLocation)}
              {unassignedTab === 1 && renderUnknownTagList(
                categorizedUnassignedTags.withoutProductInfo,
                'No scanned tags without product information were found'
              )}
            </Box>
          )}
          {tab === 4 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>Total Tags Scanned: {record.allScannedTagsList.length || record.summary.totalTagsScanned}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, border: '1px solid #e5e7eb', borderRadius: 1, p: 1 }}>
                {record.allScannedTagsList.map((tagId, index) => (
                  <CopyableTag key={index} text={tagId} variant="chip" color={tagId.includes('UNASSIGNED') ? 'warning' : 'success'} chipVariant="outlined" size="small" sx={{ fontSize: '0.7rem' }} />
                ))}
              </Box>
            </Box>
          )}
          {tab === 5 && (
            <Box>
              <Paper sx={{ p: 2, mb: 2, border: '1px solid #e5e7eb' }}>
                <Stack spacing={2}>
                  {snapshotFilters.map((filter, index) => (
                    <Grid container spacing={2} key={filter.id} alignItems="center">
                      <Grid item xs={12} sm={5} md={4}>
                        <TextField select fullWidth label={`Location condition ${index + 1}`} value={filter.locationId} onChange={(e) => updateSnapshotFilter(filter.id, 'locationId', e.target.value)}>
                          <MenuItem value="">Select location</MenuItem>
                          {(snapshot.locations || []).map(location => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <TextField select fullWidth label="Condition" value={filter.operator} onChange={(e) => updateSnapshotFilter(filter.id, 'operator', e.target.value)}>
                          <MenuItem value=">">Qty &gt;</MenuItem>
                          <MenuItem value="<">Qty &lt;</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <TextField type="number" fullWidth label="Quantity" value={filter.qty} onChange={(e) => updateSnapshotFilter(filter.id, 'qty', e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={1} md={2}>
                        <Button color="error" onClick={() => removeSnapshotFilter(filter.id)}>
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Button variant="outlined" size="small" onClick={() => setSnapshotFilters(prev => [...prev, createSnapshotFilter()])}>
                      Add condition
                    </Button>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={onlyAllLocationsGreaterThanOne}
                          onChange={(e) => setOnlyAllLocationsGreaterThanOne(e.target.checked)}
                        />
                      }
                      label="Only rows where all locations have qty > 1"
                    />
                  </Stack>
                </Stack>
              </Paper>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Snapshot captured at {formatDate(snapshot.capturedAt)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Showing {Math.min(visibleSnapshotCount, snapshotRows.length)} / {snapshotRows.length} rows
                </Typography>
              </Stack>
              <TableContainer ref={snapshotTableContainerRef} component={Paper} sx={{ border: '1px solid #e5e7eb', maxHeight: 640 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>Name</TableCell>
                      {(snapshot.locations || []).map(location => (
                        <TableCell key={location.id} align="center" sx={{ fontWeight: 700, minWidth: 130 }}>{location.name} Qty</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleSnapshotRows.map(item => (
                      <TableRow key={item.sku} hover>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        {(snapshot.locations || []).map(location => {
                          const cell = item.locations?.[String(location.id)] || { qty: 0, serials: [] };
                          return (
                            <TableCell
                              key={`${item.sku}-${location.id}`}
                              align="center"
                              onClick={() => setSerialDialog({ sku: item.sku, name: item.name, location: location.name, serials: cell.serials || [] })}
                              sx={{ cursor: 'pointer', color: cell.qty > 0 ? '#2563eb' : '#94a3b8', fontWeight: cell.qty > 0 ? 700 : 400 }}
                            >
                              {cell.qty}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {hasMoreSnapshotRows && (
                      <TableRow ref={snapshotLoadMoreRef}>
                        <TableCell colSpan={(snapshot.locations || []).length + 2} align="center" sx={{ color: '#64748b' }}>
                          Scroll to load more rows
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {hasMoreSnapshotRows && (
                <Stack alignItems="center" sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setVisibleSnapshotCount((current) => Math.min(current + SNAPSHOT_BATCH_SIZE, snapshotRows.length))}
                  >
                    Load more rows
                  </Button>
                </Stack>
              )}
            </Box>
          )}
          {tab === 6 && renderTagWrongLocationTags(record.tagWrongLocationList)}
        </CardContent>
      </Card>

      <Dialog open={!!serialDialog} onClose={() => setSerialDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Serials</Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>{serialDialog?.sku} - {serialDialog?.location}</Typography>
            </Box>
            <IconButton onClick={() => setSerialDialog(null)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {serialDialog?.serials?.length ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {serialDialog.serials.map((serial, index) => <CopyableTag key={`${serial}-${index}`} text={serial} variant="chip" chipVariant="outlined" size="small" />)}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#64748b' }}>No serials at this location.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSerialDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryCountDetailPage;
